import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { NumericInput } from "@/components/ui/numeric-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AttributeKindEnum,
  TestAttributePhaseEnum,
  type TestTemplateAttribute,
} from "@brewbase/db/types";
import UnitSelect from "@/components/unit-select";
import InlineEditableInput from "@/components/InlineEditableInput";
import { ClipboardCheckIcon } from "lucide-react";
import {
  SectionHeader,
  FlagBox,
  DefaultValuesContent,
} from "@/features/template-block-editor/inspector-helpers";
import type { InspectorExtrasArgs } from "@/features/template-block-editor/types";

const PHASE_NONE = "NONE";

const PHASE_OPTIONS: { value: string; label: string }[] = [
  { value: PHASE_NONE, label: "None" },
  { value: TestAttributePhaseEnum.PREP, label: "Prep" },
  { value: TestAttributePhaseEnum.MEASURE, label: "Measure" },
  { value: TestAttributePhaseEnum.RESULT, label: "Result" },
  { value: TestAttributePhaseEnum.REVIEW, label: "Review" },
];

export function TestInspectorExtras({
  attribute: baseAttribute,
  isLocked,
  isGenerated,
  onUpdateSettings,
}: InspectorExtrasArgs) {
  const attribute = baseAttribute as TestTemplateAttribute;
  const isSpecification = attribute.is_specification ?? false;
  const kind = attribute.kind as AttributeKindEnum;
  const phase = attribute.phase ?? null;
  // A generated field belongs to the blueprint, except its default and a
  // result's specification, which are the method's own.
  const phaseLocked = isLocked || isGenerated;
  const specLocked =
    isLocked || (isGenerated && phase !== TestAttributePhaseEnum.RESULT);

  const handlePhaseChange = (value: string) => {
    const next = value === PHASE_NONE ? null : (value as TestAttributePhaseEnum);
    onUpdateSettings({ phase: next });
  };

  return (
    <>
      {/* Phase */}
      <SectionHeader title="Phase" />
      <div className="px-3 py-3">
        <Select
          value={phase ?? PHASE_NONE}
          onValueChange={handlePhaseChange}
          disabled={phaseLocked}
        >
          <SelectTrigger className="w-full h-7 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PHASE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground mt-1">
          Each Result attribute is a column of the test in the samples grid.
        </div>
      </div>

      {/* Flags */}
      <SectionHeader title="Flags" />
      <div className="px-3 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FlagBox
            id="is-specification"
            icon={
              <ClipboardCheckIcon className="h-3 w-3 text-muted-foreground" />
            }
            label="Specification"
            description="Define target values and tolerances."
            checked={isSpecification}
            disabled={specLocked}
            onChange={(checked) => {
              onUpdateSettings({
                is_specification: checked,
                ...(!checked && {
                  spec_details: null,
                  spec_target_value_number: null,
                  spec_abs_tol_upper: null,
                  spec_abs_tol_lower: null,
                  spec_target_value_unit: null,
                  spec_target_check_value: null,
                }),
              });
            }}
          />
        </div>
      </div>

      {/* Default Values */}
      {(kind === AttributeKindEnum.SINGLE_NUMBER ||
          kind === AttributeKindEnum.CHECK) && (
        <>
          <SectionHeader title="Default Values" />
          <div className="px-3 py-3">
            <DefaultValuesContent
              kind={kind}
              attribute={attribute}
              onUpdateSettings={onUpdateSettings}
              unitLocked={isGenerated}
            />
          </div>
        </>
      )}

      {/* Specification */}
      {isSpecification && (
        <>
          <SectionHeader title="Specification" />
          <div className="px-3 py-3">
            <SpecificationContent
              kind={kind}
              attribute={attribute}
              onUpdateSettings={onUpdateSettings}
            />
          </div>
        </>
      )}
    </>
  );
}

// ======================== Specification Settings ========================

function SpecificationContent({
  kind,
  attribute,
  onUpdateSettings,
}: {
  kind?: AttributeKindEnum;
  attribute: TestTemplateAttribute;
  onUpdateSettings: (settings: Record<string, unknown>) => void;
}) {
  if (
    kind === AttributeKindEnum.SINGLE_NUMBER ||
    kind === AttributeKindEnum.COMPUTED_NUMBER
  ) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs font-medium">Target</Label>
            <NumericInput
              precise
              value={attribute.spec_target_value_number ?? null}
              onValueChange={(v) =>
                onUpdateSettings({
                  is_specification: true,
                  spec_target_value_number: v,
                })
              }
              placeholder="Target"
              className="h-7 text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Lower Tol</Label>
            <NumericInput
              precise
              value={attribute.spec_abs_tol_lower ?? null}
              onValueChange={(v) =>
                onUpdateSettings({
                  is_specification: true,
                  spec_abs_tol_lower: v,
                })
              }
              placeholder="-"
              className="h-7 text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Upper Tol</Label>
            <NumericInput
              precise
              value={attribute.spec_abs_tol_upper ?? null}
              onValueChange={(v) =>
                onUpdateSettings({
                  is_specification: true,
                  spec_abs_tol_upper: v,
                })
              }
              placeholder="+"
              className="h-7 text-sm mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium">Unit</Label>
          <UnitSelect
            value={attribute.spec_target_value_unit || ""}
            onChange={(value) =>
              onUpdateSettings({
                is_specification: true,
                spec_target_value_unit: value,
              })
            }
            placeholder="Unit"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-medium">Details</Label>
          <InlineEditableInput
            value={attribute.spec_details || ""}
            onSave={(value) =>
              onUpdateSettings({
                is_specification: true,
                spec_details: value,
              })
            }
            placeholder="Specification details..."
          />
        </div>
      </div>
    );
  }

  if (kind === AttributeKindEnum.CHECK) {
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="spec-check"
            checked={attribute.spec_target_check_value ?? false}
            onCheckedChange={(checked) =>
              onUpdateSettings({
                is_specification: true,
                spec_target_check_value: !!checked,
              })
            }
          />
          <Label htmlFor="spec-check" className="text-xs font-normal">
            Expected check value
          </Label>
        </div>
        <div>
          <Label className="text-xs font-medium">Details</Label>
          <InlineEditableInput
            value={attribute.spec_details || ""}
            onSave={(value) =>
              onUpdateSettings({
                is_specification: true,
                spec_details: value,
              })
            }
            placeholder="Specification details..."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">
      No specification settings for this attribute kind.
    </div>
  );
}
